import { HTMLMotionProps, motion } from 'framer-motion';
import { ComponentProps, ReactNode } from 'react';
import { Box, BoxProps, BoxType, Flex } from 'reflexbox';
import styled, { ThemeColors } from 'styled-components';

import * as graphql from '@treams/treams-network';

import { resizedAssetUrl } from '../helpers/resizedAssetUrl';
import { AddIcon, CityIcon, FriendsIcon, GroupsIcon, StarsIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { Skeleton } from './Skeleton';
import { Tooltip } from './Tooltip';

const AvatarContainer = styled(Box)<{ size?: number; color?: string }>`
	img {
		background: ${(props) => props.color ?? tokens.border.default};
		display: block;
		width: ${(props) => props.size ?? 64}px;
		height: ${(props) => props.size ?? 64}px;
		border-radius: ${tokens.radius.full}px;
		border: 1px solid ${tokens.border.white};
		box-sizing: border-box;
		object-fit: cover;
	}
`;

const AvatarSkeletonContainer = styled(Skeleton.Box)<{ size?: number }>`
	width: ${(props) => props.size ?? 64}px;
	height: ${(props) => props.size ?? 64}px;
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.surface.secondary};
	border: 1px solid ${tokens.border.white};
	box-sizing: border-box;
`;

const InitialsContainer = styled(Flex)<{ size?: number; color?: string }>`
	background: ${(props) => (props.color ? props.theme.colors[props.color] : tokens.border.default)};
	width: ${(props) => props.size ?? 64}px;
	height: ${(props) => props.size ?? 64}px;
	border-radius: ${tokens.radius.full}px;
	border: 1px solid ${tokens.border.white};
	box-sizing: border-box;
	align-items: center;
	justify-content: center;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${(props) => {
		const size = props.size ?? 64;
		return size < 24 ? Math.round(size * 0.4) : Math.round((size * 11) / 32);
	}}px;
	font-weight: ${tokens.typography.weights.bold};
	letter-spacing: ${(props) => ((props.size ?? 64) < 24 ? 0 : 1)}px;
	line-height: 1;
	overflow: hidden;
	color: ${tokens.text.primary};
	print-color-adjust: exact;
	-webkit-print-color-adjust: exact;
`;

const IconContainer = styled(Flex)<{ size?: number; color?: string }>`
	background: ${(props) => (props.color ? props.theme.colors[props.color] : tokens.border.default)};
	width: ${(props) => props.size ?? 64}px;
	height: ${(props) => props.size ?? 64}px;
	border-radius: ${tokens.radius.full}px;
	border: 1px solid ${tokens.border.white};
	box-sizing: border-box;
	align-items: center;
	justify-content: center;
	color: ${tokens.icon.primary};

	svg {
		display: block;
		width: ${(props) => (props.size ?? 64) - 12}px;
		height: ${(props) => (props.size ?? 64) - 12}px;
		color: inherit;
	}
`;

const GroupContainer = styled(Flex)<{ group?: graphql.Group; size?: number }>`
	background: ${(props) =>
		props.group
			? props.group.type === graphql.GroupType.Department
				? props.theme.colors.manager
				: props.theme.colors.employee
			: tokens.text.primary};
	width: ${(props) => props.size ?? 64}px;
	height: ${(props) => props.size ?? 64}px;
	border-radius: ${tokens.radius.full}px;
	border: 1px solid ${tokens.border.white};
	box-sizing: border-box;
	align-items: center;
	justify-content: center;
	color: ${tokens.text.inverse};

	svg {
		display: block;
		width: ${(props) => ((props.size ?? 64) / 3) * 2}px;
		height: ${(props) => ((props.size ?? 64) / 3) * 2}px;
		color: inherit;
	}
`;

const OverflowContainer = styled(InitialsContainer)`
	flex-shrink: 0;
	letter-spacing: -0.06em;
`;

const OverlappedAvatars = styled(Flex)`
	isolation: isolate;

	> :not(:first-child) {
		margin-left: -16px;
	}
`;

const StackSlot = styled.div<{ $layer: number }>`
	position: relative;
	z-index: ${(props) => props.$layer};
	flex-shrink: 0;
	line-height: 0;

	&:hover,
	&:focus-within {
		z-index: 50;
	}
`;

const BorderedAvatar = styled(Avatar)<{ borderColor?: keyof ThemeColors; borderWidth?: number }>`
	> div,
	img {
		border-style: solid;
		border-width: ${({ borderWidth }) => borderWidth ?? 1}px;
		border-color: ${({ theme, borderColor }) =>
			borderColor ? theme.colors[borderColor] : tokens.border.white};
	}
`;

export const Initials = function Initials(props: {
	user: Pick<graphql.User, 'firstName' | 'lastName'>;
	size?: number;
	color?: string;
}) {
	return (
		<InitialsContainer size={props.size} color={props.color}>
			{props.user.firstName[0]}
			{props.user.lastName[0]}
		</InitialsContainer>
	);
};

export type UserAvatarView = ComponentProps<typeof Initials>['user'] & {
	avatar?:
		| {
				source: Pick<NonNullable<graphql.User['avatar']>['source'], 'url'>;
		  }
		| null
		| undefined;
};

function Avatar(
	props: BoxProps & { size?: number; color?: string; user?: UserAvatarView; icon?: ReactNode },
) {
	const imageSize = (props.size ?? 64) * 2;
	const { icon, user, ...rest } = props;

	return (
		<AvatarContainer {...rest}>
			{icon && (
				<IconContainer size={props.size} color={props.color}>
					{icon}
				</IconContainer>
			)}
			{!icon && user?.avatar?.source?.url && (
				<img
					src={resizedAssetUrl(user.avatar.source.url, {
						width: imageSize,
						height: imageSize,
					})}
					alt={`${user.firstName} ${user.lastName}`}
				/>
			)}
			{/* No user (e.g. still loading): render nothing, not a blank circle. */}
			{!icon && user && !user.avatar?.source?.url && (
				<Initials size={props.size} user={user} color={props.color} />
			)}
		</AvatarContainer>
	);
}

Avatar.SystemUserID = '11111111-1111-1111-1111-111111111111';

function AvatarGroup(props: BoxProps & { group?: graphql.Group; size?: number }) {
	return (
		<GroupContainer {...props}>
			{props.group?.type === graphql.GroupType.Department && <GroupsIcon />}
			{props.group?.type === graphql.GroupType.Team && <FriendsIcon />}
			{props.group === undefined && <CityIcon />}
		</GroupContainer>
	);
}

Avatar.Group = AvatarGroup;

function AvatarSkeleton(props: BoxProps & { size?: number }) {
	return (
		<Box {...(props as ComponentProps<BoxType>)}>
			<AvatarSkeletonContainer size={props.size} />
		</Box>
	);
}

Avatar.Skeleton = AvatarSkeleton;

function AvatarOverflow(props: { count: number; size?: number }) {
	return <OverflowContainer size={props.size}>+{String(props.count)}</OverflowContainer>;
}

Avatar.Overflow = AvatarOverflow;

function AvatarList(props: { nodes: graphql.NoteCollaboratorEdge[]; limit?: number }) {
	const limit = props.limit ?? 3;
	const hasOverflow = props.nodes.length > limit + 1;
	const users = props.nodes.slice(0, hasOverflow ? limit : limit + 1);

	return (
		<>
			{users.map(({ node }) => (
				<Box key={node.id} mr={2}>
					<Avatar size={32} user={node} />
				</Box>
			))}

			{hasOverflow && (
				<Box mr={2}>
					<Avatar.Overflow size={32} count={props.nodes.length - limit} />
				</Box>
			)}
		</>
	);
}

Avatar.List = AvatarList;

function AvatarStack(props: {
	users: (Pick<graphql.User, 'id'> & NonNullable<ComponentProps<typeof Avatar>['user']>)[];
	borderColor?: keyof ThemeColors | (keyof ThemeColors | undefined)[];
	borderWidth?: number;
	shownAvatarAmount?: number;
	/** Total number of users when `users` is a page of a larger list; drives the overflow count. */
	totalCount?: number;
	small?: boolean;
	showNameTooltip?: boolean;
}) {
	const shownAvatarAmount = props.shownAvatarAmount ?? 3;
	const totalCount = Math.max(props.totalCount ?? 0, props.users.length);

	const size = props.small ? 24 : 32;

	return (
		<OverlappedAvatars alignItems="center">
			{props.users.slice(0, shownAvatarAmount).map((person, index) => {
				const avatar = (
					<BorderedAvatar
						user={{
							firstName: person.firstName,
							lastName: person.lastName,
							avatar: person.avatar ? { source: { url: person.avatar.source.url } } : undefined,
						}}
						size={size}
						borderColor={
							Array.isArray(props.borderColor) ? props.borderColor[index] : props.borderColor
						}
						borderWidth={props.borderWidth}
					/>
				);

				return (
					<StackSlot key={person.id} $layer={index + 1}>
						{props.showNameTooltip ? (
							<Tooltip
								content={`${person.firstName} ${person.lastName}`}
								position="top"
								dark
								portal
								fitContent
							>
								{avatar}
							</Tooltip>
						) : (
							avatar
						)}
					</StackSlot>
				);
			})}
			{totalCount > shownAvatarAmount && (
				<StackSlot $layer={shownAvatarAmount + 1}>
					<Avatar.Overflow size={size} count={totalCount - shownAvatarAmount} />
				</StackSlot>
			)}
		</OverlappedAvatars>
	);
}

AvatarStack.Skeleton = function AvatarStackSkeleton(props: { count?: number; small?: boolean }) {
	const size = props.small ? 24 : 32;

	return (
		<OverlappedAvatars alignItems="center">
			{[...new Array(props.count ?? 3)].map((_, i) => (
				<StackSlot key={i} $layer={i + 1}>
					<Avatar.Skeleton size={size} />
				</StackSlot>
			))}
		</OverlappedAvatars>
	);
};

Avatar.Stack = AvatarStack;

const AvatarAddCircle = styled.div<{ size: number }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${(props) => props.size}px;
	height: ${(props) => props.size}px;
	border-radius: ${tokens.radius.full}px;
	border: 1px solid ${tokens.border.default};
	box-sizing: border-box;
	color: ${tokens.icon.primary};

	svg {
		width: 20px !important;
		height: 20px !important;
		color: inherit;
	}
`;

function AvatarAdd(props: BoxProps & { size?: number }) {
	const { size, ...rest } = props;

	return (
		<AvatarAddCircle size={size ?? 64} {...rest}>
			<AddIcon />
		</AvatarAddCircle>
	);
}

Avatar.Add = AvatarAdd;

const AvatarAIContainer = styled(Avatar)`
	border: 1px solid ${tokens.border.white};
	border-radius: ${tokens.radius.full}px;

	${IconContainer} {
		width: 100%;
		height: 100%;
		padding: 10%;
		color: ${tokens.brand.icon};

		svg {
			width: 100%;
			height: 100%;
		}
	}
`;

function AvatarAI(props: Omit<ComponentProps<typeof Avatar>, 'user'>) {
	return <AvatarAIContainer color="transparent" icon={<StarsIcon />} {...props} />;
}

Avatar.AI = AvatarAI;

export type AvatarRingTone = 'brand' | 'warning';

const ringShadow = (tone: AvatarRingTone | undefined, width: number) =>
	tone ? `0 0 0 ${width}px ${tokens[tone].border}` : '0 0 0 0 transparent';

/*
 * Without a tone the ring is transparent at zero width instead of `none`, so a
 * hover ring grows in rather than popping in.
 */
const RingContainer = styled(motion.div)<{
	$tone?: AvatarRingTone;
	$width: number;
	$hoverTone?: AvatarRingTone;
	$hoverWidth: number;
}>`
	line-height: 0;
	border-radius: ${tokens.radius.full}px;
	transition: box-shadow 150ms ease-in-out;
	box-shadow: ${(props) => ringShadow(props.$tone, props.$width)};

	${(props) =>
		props.$hoverTone &&
		`
		&:hover {
			box-shadow: ${ringShadow(props.$hoverTone, props.$hoverWidth)};
		}
	`}
`;

/**
 * Ring around an avatar, drawn as a box-shadow so it does not affect layout and
 * can be animated. Accepts all motion props, e.g. `whileHover` to scale along.
 * @param props Component props.
 * @param props.tone Ring colour; without one the ring is invisible.
 * @param props.width Ring thickness in pixels.
 * @param props.hoverTone Ring colour while hovered; without one the ring does not react to hover.
 * @param props.hoverWidth Ring thickness while hovered; defaults to `width`.
 */
function AvatarRing(
	props: Omit<HTMLMotionProps<'div'>, 'ref'> & {
		tone?: AvatarRingTone;
		width?: number;
		hoverTone?: AvatarRingTone;
		hoverWidth?: number;
	},
) {
	const { tone, width = 2, hoverTone, hoverWidth, ...rest } = props;

	return (
		<RingContainer
			$tone={tone}
			$width={width}
			$hoverTone={hoverTone}
			$hoverWidth={hoverWidth ?? width}
			{...rest}
		/>
	);
}

Avatar.Ring = AvatarRing;

export { Avatar };
